/**
 * フロー図 1 枚の描画。**配置済みの節点と枝だけを受け取る。**
 *
 * 図の中身がユーザーストーリーの流れでも業務ロジックの流れでも、描く仕事は同じである —
 * 受け取った節点と枝を React Flow へ渡し、拡大・パン・全体表示ができる設定と 1 つの視点で
 * 描く。違うのは**どこへ置くか**（配置ルール）と**何を載せるか**（中身と差分の印）だけで、
 * そちらは使う側が組み立てて渡す。配置ルールをこの中へ持ち込むと、図を 1 種類増やすたびに
 * この部品が分岐を抱え、「どちらの図でも同じであるべき設定」が片方だけ変わる余地が生まれる。
 *
 * 実際にそうなっていた。かつては PJ レイヤーだけが React Flow を「絵を描くだけの箱」に落とし、
 * 倍率を固定して（`viewport` に固定値を渡し、`zoomOnScroll` / `zoomOnPinch` /
 * `zoomOnDoubleClick` / `panOnDrag` を全部切って）外側の器を素のスクロールで動かしていた。
 * 結果、現在版では当たり前にできる拡大・パン・全体表示が PJ 側だけ消え、同じ図なのに読み方が
 * 画面で違った。
 *
 * **縮尺と左右の同期は枠から受け取る。** 初期表示の縮尺（図が枠に入る倍率、下限 0.75）と、
 * 変更前と並べたときの視点の同期は、どの図でも同じであるべき設定なので、枠（`FlowWorkspace`）が
 * 文脈で配ったものをここで当てる。並べた 2 枚の視点が別々に動くと、片方だけ拡大した状態で
 * 「並べて比べる」ことになり、同じ大きさの箱が左右で違う大きさに描かれる。
 *
 * **レーン名は縮尺の外に描く。** 図の中身は React Flow が `transform: scale()` で縮めるので、
 * その中に置いたレーン名は図と一緒に縮み、横へ動かすと枠の外へ流れていく。レーン名は視点を読んで
 * 枠の左端に留める別の層として描き、縦の位置と帯の高さだけを図に合わせる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 配置済みの節点と枝を受け取り、拡大・パンできるフロー図 1 枚として描く。
 * @graph-connects FlowGutterEdge [references] 作図規約に沿った線の描画
 * @graph-connects useFlowPaneContext [calls] 枠が配った縮尺・左右比較・視点の同期
 */
import {
  Background,
  Controls,
  ReactFlow,
  useReactFlow,
  useViewport,
  type Edge,
  type Node,
  type NodeTypes,
  type OnMove,
  type Viewport,
} from '@xyflow/react';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailSection } from '../../components/spec/DetailSection.js';
import { formatPjLabel } from '../../lib/pj-label.js';
import { useMeasuredWidth } from '../flow-pane-width.js';
import { FLOW_EDGE_TYPES } from '../FlowGutterEdge.js';
import { MIN_FIT_SCALE } from '../user-story-flow-layout.js';
import { useFlowPaneContext, type FlowViewportSync } from './flow-pane-context.js';
import { scaleViewportOf } from './flow-scale.js';

import '@xyflow/react/dist/style.css';

/** 図の表示領域。高さは配置の高さと全画面かどうかから決まるので、ここでは持たない。 @graph-connects none */
const CANVAS_CLASS = 'w-full border border-[var(--color-border)]';

/** 静かな枠の拡大・縮小のボタン。図に乗った時とキーボードで辿った時だけ出す。 @graph-connects none */
const QUIET_CONTROL_CLASS =
  'opacity-0 transition-opacity group-hover/canvas:opacity-100 focus-within:opacity-100';

/**
 * 視点の持ち方。図が自分で視点を持ち、最初に置く視点を**実寸で**受け取る。
 *
 * 縮尺は受け取った視点に図の面が当てる（`scaleViewportOf`）。左右 2 枚の視点を揃えるのも
 * 枠から配られた同期の口が担うので、ここで受け取るのは最初の位置だけである。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図が自分で持つ視点と、その最初の位置（実寸）を表す。
 * @graph-connects none
 */
export interface FlowViewportControl {
  /** 図が自分で視点を持つ。 */
  readonly kind: 'own';
  /** 最初に置く視点（実寸）。 */
  readonly initial: Viewport;
}

/**
 * 縮尺の外に描くレーン名 1 本。座標は図の中の値（実寸）で持つ。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の左端に留めて描くレーン名 1 本の位置と中身を表す。
 * @graph-connects none
 */
export interface FlowLaneLabel {
  /** 描き分けの鍵。 */
  key: string;
  /** 帯の上端（実寸）。 */
  y: number;
  /** 帯の高さ（実寸）。 */
  height: number;
  /** レーン名として出す中身。 */
  label: ReactNode;
}

/**
 * 縮尺の外に描くレーン名の並び。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の左端に留めて描くレーン名の並びと、その列の幅を表す。
 * @graph-connects none
 */
export interface FlowLaneLabels {
  /** レーン名。上から順。 */
  rows: readonly FlowLaneLabel[];
  /** レーン名の列の幅（実寸）。配置が節点の左に空けている幅と揃える。 */
  width: number;
}

/**
 * `FlowCanvas` の props。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図 1 枚を描くのに要る見出し・寸法・節点・枝・視点・レーン名を表す。
 * @graph-connects none
 */
export interface FlowCanvasProps {
  /** 区画の見出し。左右比較では見出しを面の名前（変更前 / 変更後）に替え、ここは読み上げにだけ使う。 */
  title: string;
  /** 検証で面を見分けるための名前。左右比較では `before` / `after`。 */
  side: string;
  /** 図の器の高さ。 */
  height: number;
  /** 配置済みの節点。座標も中身も組み立て済みで渡す。 */
  nodes: Node[];
  /** 配置済みの枝。経路も印も組み立て済みで渡す。 */
  edges: Edge[];
  /** 視点の持ち方。 */
  viewportControl: FlowViewportControl;
  /** 縮尺の外に描くレーン名。レーンを持たない図では渡さない。 */
  laneLabels?: FlowLaneLabels;
  /**
   * 独自の節点の型。線の出入り口を右端以外にも持つ節点（ロジック図の業務ルールから下の表へ引く点線）
   * を描く図だけが渡す。省けば React Flow の既定の節点で描く。
   */
  nodeTypes?: NodeTypes;
  /**
   * 図の中（React Flow の文脈の内側）に置く、描かない部品。
   *
   * 選んだ節点へ視点を寄せる部品のように、図の視点を読んで動かすものは React Flow の文脈の
   * 内側でしか視点に触れない。
   */
  children?: ReactNode;
}

/**
 * 配置済みの節点と枝を、区画の見出しつきの図 1 枚として描く。
 *
 * @param props 見出し・面の名前・高さ・節点・枝・視点の持ち方・レーン名
 * @returns 区画に収めたフロー図 1 枚
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 配置済みの節点と枝を、枠の決めた縮尺と左右の同期つきで、拡大・パンできるフロー図 1 枚として描く。
 * @graph-connects FlowGutterEdge [references] 作図規約に沿った線の描画
 * @graph-connects useFlowPaneContext [calls] 枠が配った縮尺・左右比較・視点の同期
 * @graph-connects scaleViewportOf [calls] 実寸の視点への縮尺の当て方
 * @graph-connects useMeasuredWidth [calls] 図の器の表示幅の実測
 */
export function FlowCanvas({
  title,
  side,
  height,
  nodes,
  edges,
  viewportControl,
  laneLabels,
  nodeTypes,
  children,
}: FlowCanvasProps) {
  const {
    scale,
    reportPaneWidth,
    paneMeasured,
    comparePj,
    sync,
    quiet = false,
  } = useFlowPaneContext();
  const initial = scaleViewportOf(viewportControl.initial, scale);

  /*
   * 自分の器を測って枠へ知らせる。縮尺は面の幅から決まり、面の幅として正しいのはこの器の
   * 幅である（1 面のときは区画の余白の内側に入るので、枠が測る格子より狭い）。
   */
  const { width: paneWidth, measured, attach } = useMeasuredWidth();
  useEffect(() => {
    if (measured && reportPaneWidth !== null) reportPaneWidth(paneWidth);
  }, [measured, paneWidth, reportPaneWidth]);

  /*
   * 読み手の操作から来た動きだけをもう片方へ写す。写された側の図も視点が動いたことを知らせて
   * くるが、そのときの `event` は null（プログラムからの移動）なので送り返さない。送り返すと
   * 2 面が互いに書き合って止まらない。
   */
  const onMove = useCallback<OnMove>(
    (event, viewport) => {
      if (sync !== null && event !== null) sync.publish(side, viewport);
    },
    [sync, side],
  );

  const canvas = (
    <div
      ref={attach}
      className={quiet ? `${CANVAS_CLASS} group/canvas` : CANVAS_CLASS}
      style={{ height }}
      data-flow-pane={side}
      aria-label={title}
    >
      {paneMeasured && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          edgeTypes={FLOW_EDGE_TYPES}
          {...(nodeTypes === undefined ? {} : { nodeTypes })}
          nodesConnectable={false}
          nodesDraggable={false}
          defaultViewport={initial}
          onMove={onMove}
          /*
           * 手で縮める下限は初期表示の下限（0.75）より下げる。全体の形を一目で見たい読み手は
           * 自分で縮められる。初期表示を読める大きさで止めるのと、読み手が縮めるのは別の判断である。
           */
          minZoom={MIN_FIT_SCALE}
          /*
           * ホイールは**移動**に割り当てる。既定はホイールが拡大縮小で、図が枠より大きいこの
           * 画面では「読みたい場所へ寄せる」たびに縮尺が変わり、目的の場所へ辿り着けない。
           * ブラウザや地図と同じ割り当て（そのまま縦移動、shift で横移動、ctrl / cmd で拡大縮小）
           * に揃える。shift と ctrl の扱いは React Flow が `panOnScroll` のときに自前で持っている。
           */
          panOnScroll
          zoomOnScroll={false}
        >
          <Background />
          <RescaleOnChange scale={scale} initial={initial} />
          {sync !== null && <ViewportSyncMember side={side} sync={sync} />}
          {/* レーン名は操作の部品より先に置く。同じ重なり順なら後に置いたほうが上に描かれる。 */}
          {laneLabels !== undefined && <PinnedLaneLabels {...laneLabels} />}
          {/*
            静かな枠では、拡大・縮小・全体に合わせるのボタンを図に乗った時（とキーボードで辿った時）
            だけ出す。いつも出すと、図を読むたびに操作の部品が目に入る。
          */}
          <Controls
            showInteractive={false}
            {...(quiet ? { className: QUIET_CONTROL_CLASS } : {})}
          />
          {children}
        </ReactFlow>
      )}
    </div>
  );

  return (
    /*
     * **格子の項目に `min-width: 0` を与える。** 格子と伸縮箱の項目は既定で `min-width: auto` を
     * 持ち、中身の最小幅より狭くならない。区画をそのまま項目に置くと、器は割り当てられた列では
     * なく図の中身の幅まで広がる。本番でそうなっていた（実測: 器 11812px・割り当てられた列 1798px）。
     */
    <div className="min-w-0">
      {comparePj !== null && (side === 'before' || side === 'after') ? (
        <section aria-label={title} data-compare-pane={side}>
          <ComparePaneHeading side={side} pjNumber={comparePj} />
          {canvas}
        </section>
      ) : quiet ? (
        /* 静かな枠の 1 面は見出しを出さない。図であることは見れば分かる。名前は読み上げにだけ残す。 */
        <section aria-label={title}>{canvas}</section>
      ) : (
        <DetailSection title={title}>{canvas}</DetailSection>
      )}
    </div>
  );
}

/**
 * 左右比較の面の見出し。変更前は赤、変更後は緑のベタに背景色の文字で出す。
 *
 * 左右とも塗るのは、面の地が赤（削除）と緑（追加）に分かれた以上、色の意味を見出しで
 * 確かめられる必要があるためである。語と色は表や一覧の左右比較（`DiffSplitPanes`）と揃える。
 *
 * 字は**全画面で「変更前」「変更後」の 2 語だけ**にし、「公開中の仕様」と案件番号は見出しを指すと
 * 出す（Dhik 2026-09-29）。図によって添え書きの有無が違うと、同じ比較が画面で違う姿に見える。
 * 中身が空の面（業務フローの図で片側に節点が無いとき）も、この見出しを使って図の面と揃える。
 *
 * @param props 面の名前と案件番号
 * @returns 面の見出し
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右比較の面の見出しを、変更前は赤・変更後は緑のベタに「変更前」「変更後」の 2 語で出し、公開中の仕様と案件番号は指すと出す。
 * @graph-connects formatPjLabel [calls] 案件番号の表記
 */
export function ComparePaneHeading({
  side,
  pjNumber,
}: {
  side: 'before' | 'after';
  pjNumber: number;
}) {
  const { t } = useTranslation();
  const tone =
    side === 'before' ? 'bg-[var(--color-diff-removed-pane)]' : 'bg-[var(--color-diff-added-pane)]';
  return (
    <h2
      title={side === 'before' ? t('specDiff.paneBeforeNote') : formatPjLabel(pjNumber)}
      className={`min-w-0 px-2 py-1.5 text-xs font-bold text-[var(--color-bg-base)] ${tone}`}
    >
      {side === 'before' ? t('specDiff.paneBefore') : t('specDiff.paneAfter')}
    </h2>
  );
}

/**
 * 縮尺が変わったら、最初の位置を新しい縮尺で置き直す（描かない部品）。
 *
 * React Flow は最初の視点を最初の描画でしか読まない。左右比較への切り替え・全画面・画面の
 * 幅の変化で面の幅が変わると縮尺も変わるが、置き直さないと古い縮尺のまま残り、左右で
 * 縮尺が食い違う。最初の描画では動かさない（そこは `defaultViewport` が置いている）。
 *
 * @param props 今の縮尺と、その縮尺を当てた最初の位置
 * @returns 何も描かない
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の縮尺が変わったときに、最初の位置を新しい縮尺で置き直す。
 * @graph-connects none
 */
function RescaleOnChange({ scale, initial }: { scale: number; initial: Viewport }) {
  const { setViewport } = useReactFlow();
  const latest = useRef(initial);
  latest.current = initial;
  const applied = useRef(scale);

  useEffect(() => {
    if (applied.current === scale) return;
    applied.current = scale;
    void setViewport(latest.current);
  }, [scale, setViewport]);

  return null;
}

/**
 * 左右比較の同期の口へ、この面の視点を書き換える口を登録する（描かない部品）。
 *
 * @param props 面の名前と同期の口
 * @returns 何も描かない
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右比較のもう片方で動いた視点を、この面へ写せるようにする。
 * @graph-connects none
 */
function ViewportSyncMember({ side, sync }: { side: string; sync: FlowViewportSync }) {
  const { setViewport } = useReactFlow();

  useEffect(
    () =>
      sync.register(side, (viewport) => {
        void setViewport(viewport);
      }),
    [side, sync, setViewport],
  );

  return null;
}

/**
 * レーン名を、縮尺の外で枠の左端に留めて描く。
 *
 * 縦の位置と帯の高さは今の視点（縮尺と縦の移動）に合わせ、横は動かさない。図の中（縮尺の内側）に
 * 置くと、`transform` が包含ブロックになって貼り付き（sticky）が効かず、横へ動かしたとたんに
 * どの帯が誰のレーンかが読めなくなる。文字は縮めない。読み手が名前を読むための層である。
 *
 * @param props レーン名の並びと列の幅
 * @returns 枠の左端に留めたレーン名の層
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン名を図の縮尺の外で枠の左端に留め、縦の位置と帯の高さだけを図に合わせて描く。
 * @graph-connects none
 */
function PinnedLaneLabels({ rows, width }: FlowLaneLabels) {
  const { y, zoom } = useViewport();

  return (
    <div className="pointer-events-none absolute inset-0 z-[5] overflow-hidden" data-lane-labels="">
      {rows.map((row) => (
        <div
          key={row.key}
          className="absolute left-0 overflow-hidden border-t border-r-2 border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 pt-2 text-xs font-semibold text-[var(--color-text-muted)]"
          style={{ top: row.y * zoom + y, height: row.height * zoom, width: width * zoom }}
        >
          {row.label}
        </div>
      ))}
    </div>
  );
}
